/**
 * @description
 * Defines an interface that acts converts filter models into a value for @vendasta/forms/input-tags/Option
 *
 * Implement this interface to create a custom filter option for @angular/forms/ControlValueAccessor
 * that can integrate with Vendasta forms InputTags.
 * @usageNotes
 * ### Declares a model with a string option
 * ```ts
 * interface M {
 *  id: string
 *  field: string;
 * }
 *
 * class MyModel extends AbstractOption<string, M> implements M {
 *  id: string
 *  field: string;
 *
 *   constructor(m: M) {
 *     super();
 *     this.id = m.id;
 *     this.field = m.v;
 *     this.optionValue = [this.id, this.field].join(',');
 *   }
 *   static fromOptionValue(option: string): SalespersonOption {
 *     const tup = option.split(delimiter);
 *     return new SalespersonOption({id: tup[0], name: tup[1]});
 *   }
 * }
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export abstract class AbstractOption<OPTION, MODEL> {
  private _optionValue: OPTION;

  // TODO declare abstract static function
  // currently not supported in typescript, issue number: https://github.com/microsoft/TypeScript/issues/34516#issuecomment-544404500
  // abstract static fromOptionValue(option: OPTION): MODEL {

  protected get optionValue(): OPTION {
    return this._optionValue;
  }

  protected set optionValue(v: OPTION) {
    this._optionValue = v;
  }

  /**
   * @description
   * Converts the child class (MODEL) to an Option value (OPTION).
   *
   * @usageNotes
   * ### Converts the object to option value
   *
   * The following example converts a MODEL to an OPTION.
   * ```html
   * <va-input-tags
   *   [allowDuplicates]="false"
   *   [options]="options"
   *   [formControl]="fc"
   * ></va-input-tags>
   * ```
   *
   * ```ts
   * import { Option } from '@vendasta/forms';
   * ...
   * allModels: MyModel[] = ...;
   * selectedModels: MyModel[] = ...;
   * options: Option[] = models.map(m => ({name: m.name, value: m.toOptionValue()});
   * fc: FormControl = new FormControl([], []);
   * ...
   * const selected = models.map(m => m.toOptionValue());
   * this.fc.setValue(selected);
   * ```
   *
   * @return OPTION type
   */
  toOptionValue(): OPTION {
    return this.optionValue;
  }
}
